import React, { useState } from 'react';
import {
  BarChart, Bar, LineChart, Line,
  XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
} from 'recharts';
import {
  Card, CardHeader, CardTitle, CardBody, Spinner,
  Button,
} from '@patternfly/react-core';
import { SimpleSelect } from '@patternfly/react-templates';
import { ToggleBtn } from '@app/components/ToggleBtn';
import { useEventNames, useEventTimeSeries, useRawEvents } from '../hooks/useUsageAnalytics';
import { Granularity, ChartType, GRANULARITY_OPTIONS, DAYS_OPTIONS, TimeSeriesPoint } from '../types';

const CHART_COLOR = '#0066cc';

function formatPeriod(iso: string, granularity: Granularity): string {
  const d = new Date(iso);
  if (granularity === 'monthly') return d.toLocaleDateString('en-US', { month: 'short', year: 'numeric' });
  if (granularity === 'weekly') return `W/c ${d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}`;
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
}

function exportCsvDetailed(rawEvents: Array<{ timestamp: string; user_email: string; user_name: string; event_name: string; source_cluster: string }>, eventName: string) {
  const header = 'Timestamp,User Name,User Email,Event Name,Cluster\n';
  const body = rawEvents
    .map((r) => `"${r.timestamp}","${r.user_name}","${r.user_email}","${r.event_name}","${r.source_cluster}"`)
    .join('\n');
  const blob = new Blob([header + body], { type: 'text/csv' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `event-calls-${eventName}-detailed-${new Date().toISOString().slice(0, 10)}.csv`;
  a.click();
  URL.revokeObjectURL(url);
}

interface EventTimeSeriesChartProps {
  cluster?: string | null;
}

export const EventTimeSeriesChart: React.FC<EventTimeSeriesChartProps> = ({ cluster }) => {
  const [selectedEvent, setSelectedEvent] = useState<string | null>(null);
  const [granularity, setGranularity] = useState<Granularity>('daily');
  const [days, setDays] = useState<number>(7);
  const [chartType, setChartType] = useState<ChartType>('bar');
  const [isExporting, setIsExporting] = useState(false);

  const { eventNames, isLoading: namesLoading, error: namesError } = useEventNames();
  const { data, isLoading: dataLoading, isFetching, error: dataError } = useEventTimeSeries(
    selectedEvent, granularity, days, cluster,
  );
  const { data: rawEvents, isLoading: rawLoading, error: rawError } = useRawEvents(selectedEvent, days, cluster);

  const chartData = data.map((p: TimeSeriesPoint) => ({
    period: formatPeriod(p.period, granularity),
    count: p.count,
  }));

  const selectOptions = eventNames.map((name) => ({ value: name, content: name }));

  const sharedChartProps = { margin: { top: 16, right: 24, left: 0, bottom: 4 }, data: chartData };
  const sharedChildren = (
    <>
      <CartesianGrid strokeDasharray="none" stroke="#f3f4f6" vertical={false} />
      <XAxis dataKey="period" tickLine={false} axisLine={false} tick={{ fontSize: 12, fill: '#6b7280' }} tickMargin={8} />
      <YAxis tickLine={false} axisLine={false} tick={{ fontSize: 12, fill: '#6b7280' }} tickMargin={8} allowDecimals={false} />
      <Tooltip
        contentStyle={{ background: '#fff', border: '1px solid #d2d2d2', borderRadius: 4, fontSize: 12 }}
        formatter={(v: number) => [v.toLocaleString(), 'Calls']}
      />
    </>
  );

  const handleExport = async () => {
    setIsExporting(true);
    try {
      if (rawEvents && rawEvents.length > 0) {
        exportCsvDetailed(rawEvents, selectedEvent || 'event');
      }
    } finally {
      setIsExporting(false);
    }
  };

  return (
    <Card>
      <CardHeader
        actions={{
          actions: chartData.length > 0 && selectedEvent ? (
            <Button 
              variant="link" 
              isInline 
              isLoading={isExporting} 
              disabled={isExporting}
              onClick={handleExport}
            >
              ↓ CSV (detailed)
            </Button>
          ) : undefined,
        }}
      >
        <CardTitle>
          Calls by Event
          {selectedEvent && (
            <span className="ua-event-badge">({selectedEvent})</span>
          )}
        </CardTitle>
      </CardHeader>
      <CardBody>
        <div className="ua-controls">
          <div className="ua-select-container">
            {namesLoading ? (
              <span className="ua-empty-state">Loading events… <Spinner size="sm" /></span>
            ) : namesError ? (
              <span className="ua-error">Failed to load events: {namesError}</span>
            ) : (
              <SimpleSelect
                initialOptions={selectOptions}
                onSelect={(_e, value) => setSelectedEvent(value as string)}
                placeholder="Select an event"
              />
            )}
          </div>
          <div className="ua-btn-group">
            {GRANULARITY_OPTIONS.map((o) => (
              <ToggleBtn key={o.value} label={o.label} active={granularity === o.value} onClick={() => setGranularity(o.value)} />
            ))}
          </div>
          <div className="ua-btn-group">
            {DAYS_OPTIONS.map((o) => (
              <ToggleBtn key={o.value} label={o.label} active={days === o.value} onClick={() => setDays(o.value)} />
            ))}
          </div>
          <div className="ua-btn-group">
            <ToggleBtn label="Bar" active={chartType === 'bar'} onClick={() => setChartType('bar')} />
            <ToggleBtn label="Line" active={chartType === 'line'} onClick={() => setChartType('line')} />
          </div>
          {(isFetching || rawLoading) && <Spinner size="sm" />}
        </div>

        {dataError && <div className="ua-error">Error: {dataError}</div>}
        {rawError && <div className="ua-error">CSV export error: {rawError}</div>}

        {!selectedEvent && (
          <div className="ua-empty-state">
            Select an event above to view its call history.
          </div>
        )}

        {selectedEvent && (
          dataLoading ? (
            <div className="ua-empty-state"><Spinner /></div>
          ) : chartData.length === 0 ? (
            <div className="ua-empty-state">
              No data for <strong>{selectedEvent}</strong> in the selected period.
            </div>
          ) : (
            <div className="ua-chart-container">
              <ResponsiveContainer width="100%" height="100%">
                {chartType === 'bar' ? (
                  <BarChart {...sharedChartProps}>
                    {sharedChildren}
                    <Bar dataKey="count" fill={CHART_COLOR} name="Calls" radius={[3, 3, 0, 0]} />
                  </BarChart>
                ) : (
                  <LineChart {...sharedChartProps}>
                    {sharedChildren}
                    <Line type="monotone" dataKey="count" stroke={CHART_COLOR} strokeWidth={2} dot={false} name="Calls" />
                  </LineChart>
                )}
              </ResponsiveContainer>
            </div>
          )
        )}
      </CardBody>
    </Card>
  );
};
